import { forwardRef } from 'react';
import Drawer, { type DrawerProps } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import { keyframes } from '@mui/system';

// ============================================================================
// ENTERPRISE ANIMATION CONFIGURATION
// Inspired by Linear, Notion, and Figma drawer patterns
// ============================================================================

// Snappy, professional timing (faster feels more responsive)
const DRAWER_TRANSITION_DURATION = {
  enter: 280,
  exit: 200, // Exit is faster for snappy feel
};

// Professional easing curves
// Enter: Deceleration curve - starts fast, eases to stop (feels responsive)
// Exit: Acceleration curve - starts slow, speeds up (feels natural)
const DRAWER_EASING = {
  enter: 'cubic-bezier(0.16, 1, 0.3, 1)', // Expo out - smooth deceleration
  exit: 'cubic-bezier(0.4, 0, 1, 1)',     // Ease in - natural acceleration
};

// Content fade-in animation for polished feel
const contentFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export interface AppDrawerProps extends Omit<DrawerProps, 'title'> {
  /**
   * Drawer title
   */
  title?: React.ReactNode;
  /**
   * Optional subtitle
   */
  subtitle?: React.ReactNode;
  /**
   * Width of the drawer (default: 480)
   */
  width?: number | string;
  /**
   * Footer content (sticky at bottom)
   */
  footer?: React.ReactNode;
  /**
   * Show close button in header
   */
  showCloseButton?: boolean;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * Whether the drawer is in a loading/submitting state
   */
  loading?: boolean;
}

/**
 * AppDrawer - Side panel component
 *
 * Wraps MUI Drawer with our design system patterns.
 * Use for forms, detail views, and secondary navigation.
 */
export const AppDrawer = forwardRef<HTMLDivElement, AppDrawerProps>(
  (
    {
      title,
      subtitle,
      width = 480,
      footer,
      showCloseButton = true,
      onClose,
      loading = false,
      children,
      ...props
    },
    ref
  ) => {
    const isOpen = props.open ?? false;

    return (
      <Drawer
        ref={ref}
        anchor="right"
        onClose={loading ? undefined : onClose}
        transitionDuration={DRAWER_TRANSITION_DURATION}
        SlideProps={{
          easing: DRAWER_EASING,
        }}
        PaperProps={{
          sx: {
            width,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            // Layered shadow for depth - animates with drawer
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '-8px 0 32px rgba(0, 0, 0, 0.12), -2px 0 8px rgba(0, 0, 0, 0.08)'
                : '-8px 0 32px rgba(0, 0, 0, 0.4), -2px 0 8px rgba(0, 0, 0, 0.2)',
            // Subtle border for definition
            borderLeft: (theme) =>
              theme.palette.mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.06)'
                : '1px solid rgba(255, 255, 255, 0.06)',
            // Hardware acceleration for smooth animation
            transform: 'translateZ(0)',
            willChange: 'transform',
          },
        }}
        // Professional backdrop with subtle blur
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(15, 23, 42, 0.4)'  // Slate-900 with opacity
                  : 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              transition: `opacity ${DRAWER_TRANSITION_DURATION.enter}ms ${DRAWER_EASING.enter}, backdrop-filter ${DRAWER_TRANSITION_DURATION.enter}ms ${DRAWER_EASING.enter}`,
            },
          },
        }}
        {...props}
      >
        {/* Header with subtle animation */}
        {(title || showCloseButton) && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                p: 3,
                flexShrink: 0,
                // Staggered fade-in animation
                animation: isOpen
                  ? `${contentFadeIn} 350ms ${DRAWER_EASING.enter} 80ms both`
                  : 'none',
              }}
            >
              <Box>
                {title && (
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      opacity: 0.8,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {showCloseButton && onClose && (
                <IconButton
                  aria-label="close drawer"
                  onClick={onClose}
                  disabled={loading}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    mt: -0.5,
                    mr: -0.5,
                    transition: 'all 150ms ease',
                    '&:hover': {
                      color: 'text.primary',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.04)'
                          : 'rgba(255, 255, 255, 0.06)',
                      transform: 'scale(1.05)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Divider
              sx={{
                opacity: 0.6,
              }}
            />
          </>
        )}

        {/* Content with staggered animation */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            // Staggered fade-in (slightly delayed from header)
            animation: isOpen
              ? `${contentFadeIn} 400ms ${DRAWER_EASING.enter} 120ms both`
              : 'none',
            // Smooth scrolling
            scrollBehavior: 'smooth',
            // Custom scrollbar
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.15)'
                  : 'rgba(255, 255, 255, 0.15)',
              borderRadius: 3,
              '&:hover': {
                background: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.25)'
                    : 'rgba(255, 255, 255, 0.25)',
              },
            },
          }}
        >
          {children}
        </Box>

        {/* Footer with animation */}
        {footer && (
          <>
            <Divider sx={{ opacity: 0.6 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                p: 2,
                px: 3,
                bgcolor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(248, 250, 252, 0.8)' // Subtle slate tint
                    : 'rgba(15, 23, 42, 0.5)',
                flexShrink: 0,
                // Staggered fade-in (last element)
                animation: isOpen
                  ? `${contentFadeIn} 350ms ${DRAWER_EASING.enter} 160ms both`
                  : 'none',
                // Subtle top shadow for elevation
                boxShadow: (theme) =>
                  theme.palette.mode === 'light'
                    ? '0 -1px 3px rgba(0, 0, 0, 0.03)'
                    : '0 -1px 3px rgba(0, 0, 0, 0.1)',
              }}
            >
              {footer}
            </Box>
          </>
        )}
      </Drawer>
    );
  }
);

AppDrawer.displayName = 'AppDrawer';
